/**
 * Steam Search — Filtering, Sorting & Search JS
 *
 * ════════════════════════════════════════════════
 *  HOW SHOPIFY FILTERING ACTUALLY WORKS (native):
 *
 *  Collection tag filtering (NO admin setup needed):
 *    /collections/all/action          ← single tag
 *    /collections/all/action+indie    ← multiple tags (+ separated handles)
 *    ?sort_by=price-ascending         ← appended as query param
 *
 *  Search page:
 *    /search?q=TERM&type=product
 *    /search?q=TERM&type=product&sort_by=price-ascending
 *
 *  Section Rendering API (AJAX partial refresh):
 *    Append &section_id=steam-search to any valid Shopify URL
 *    → Shopify returns ONLY the html for that section
 * ════════════════════════════════════════════════
 */

(function () {
  'use strict';

  /* ── Detect page context ─────────────────────────────────────── */
  var IS_SEARCH     = window.location.pathname.indexOf('/search') === 0;
  var IS_COLLECTION = window.location.pathname.indexOf('/collections') === 0;

  /* Extract collection handle from URL, e.g. "all" from /collections/all/... */
  var COL_HANDLE = 'all';
  if (IS_COLLECTION) {
    var colParts = window.location.pathname.replace(/^\/collections\//, '').split('/');
    COL_HANDLE = colParts[0] || 'all';
  }

  /* ── Price stops (matching Steam reference) ──────────────────── */
  var PRICE_STOPS = [
    { value: 'free', label: 'Free' },
    { value: '5',    label: 'Under $5.00' },
    { value: '10',   label: 'Under $10.00' },
    { value: '15',   label: 'Under $15.00' },
    { value: '20',   label: 'Under $20.00' },
    { value: '25',   label: 'Under $25.00' },
    { value: '30',   label: 'Under $30.00' },
    { value: '35',   label: 'Under $35.00' },
    { value: '40',   label: 'Under $40.00' },
    { value: '45',   label: 'Under $45.00' },
    { value: '50',   label: 'Under $50.00' },
    { value: '55',   label: 'Under $55.00' },
    { value: '60',   label: 'Under $60.00' },
    { value: '',     label: 'Any Price' }     // index 13 = no filter
  ];

  /* ── State ───────────────────────────────────────────────────── */
  var state = {
    tags:    [],   // included tag handles, e.g. ["action", "indie"]
    sortBy:  '',
    searchQ: '',
    maxPrice: '',  // e.g. "20" or "free" or "" (any)
    page:    1
  };

  /* ── Helpers ─────────────────────────────────────────────────── */
  function handleize(str) {
    return str.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /* ── Build the canonical URL for current state ───────────────── */
  function buildURL(forFetch) {
    var params = new URLSearchParams();
    var path;

    if (IS_SEARCH) {
      /* ── Search page URL ── */
      path = '/search';
      if (state.searchQ) params.set('q', state.searchQ);
      params.set('type', 'product');
      if (state.sortBy)  params.set('sort_by', state.sortBy);
      if (state.page > 1) params.set('page', state.page);
      /* price — client-side only for search (no server param available without native filters) */

    } else {
      /* ── Collection page URL ── */
      /* Tags go into the PATH, not query params */
      path = '/collections/' + COL_HANDLE;
      if (state.tags.length > 0) {
        path += '/' + state.tags.join('+');
      }
      if (state.sortBy)   params.set('sort_by', state.sortBy);
      if (state.page > 1) params.set('page', state.page);
    }

    if (forFetch) params.set('section_id', 'steam-search');

    var qs = params.toString();
    return path + (qs ? '?' + qs : '');
  }

  /* ── Section Rendering API fetch ────────────────────────────── */
  var fetchTimer = null;

  function scheduleRefresh(delayMs) {
    clearTimeout(fetchTimer);
    fetchTimer = setTimeout(doRefresh, delayMs != null ? delayMs : 0);
  }

  function doRefresh() {
    var container = document.getElementById('search_result_container');
    if (!container) {
      /* fallback: full navigation */
      window.location.href = buildURL(false);
      return;
    }

    var loading = document.getElementById('search_results_loading');
    if (loading) loading.style.display = 'block';
    container.style.opacity = '0.35';
    container.style.pointerEvents = 'none';

    var fetchURL = buildURL(true);

    fetch(fetchURL, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        /* Swap result rows */
        var newRows = doc.getElementById('search_resultsRows');
        var oldRows = document.getElementById('search_resultsRows');
        if (newRows && oldRows) oldRows.innerHTML = newRows.innerHTML;

        /* Swap pagination */
        var newPag = doc.querySelector('.search_pagination');
        var oldPag = document.querySelector('.search_pagination');
        if (oldPag) {
          if (newPag) { oldPag.outerHTML = newPag.outerHTML; }
          else { oldPag.style.display = 'none'; }
        } else if (newPag) {
          var loadingEl = document.getElementById('search_results_loading');
          if (loadingEl) loadingEl.insertAdjacentHTML('beforebegin', newPag.outerHTML);
        }

        /* Swap result count */
        var newCount = doc.querySelector('.search_results_count');
        var oldCount = document.querySelector('.search_results_count');
        if (newCount && oldCount) oldCount.innerHTML = newCount.innerHTML;

        /* Swap price-filtered results (client-side price filter for search) */
        if (IS_SEARCH && state.maxPrice) applyClientPriceFilter();

        container.style.opacity = '1';
        container.style.pointerEvents = '';
        if (loading) loading.style.display = 'none';

        /* Re-bind pagination */
        bindPaginationLinks();

        /* Push URL so back/forward works */
        history.pushState(null, '', buildURL(false));
      })
      .catch(function (err) {
        console.warn('[SteamSearch] fetch failed, falling back to navigation:', err.message);
        window.location.href = buildURL(false);
      });
  }

  /* ── Client-side price filter (hides rows outside selected range) ── */
  function applyClientPriceFilter() {
    if (!state.maxPrice) {
      /* show all */
      document.querySelectorAll('.search_result_row').forEach(function (r) { r.style.display = ''; });
      return;
    }
    if (state.maxPrice === 'free') {
      document.querySelectorAll('.search_result_row').forEach(function (r) {
        var freeEl = r.querySelector('.discount_final_price.free');
        r.style.display = freeEl ? '' : 'none';
      });
      return;
    }
    var max = parseFloat(state.maxPrice);
    document.querySelectorAll('.search_result_row').forEach(function (r) {
      var priceEl = r.querySelector('.discount_final_price');
      if (!priceEl) { r.style.display = 'none'; return; }
      var priceText = priceEl.textContent.replace(/[^0-9.]/g, '');
      var price = parseFloat(priceText);
      r.style.display = (isNaN(price) || price <= max) ? '' : 'none';
    });
  }

  /* ── Sort dropdown ───────────────────────────────────────────── */
  function initSort() {
    var el = document.getElementById('steam_sort_select');
    if (!el) return;
    el.addEventListener('change', function () {
      state.sortBy = this.value;
      state.page = 1;
      scheduleRefresh(0);
    });
  }

  /* ── Search input ────────────────────────────────────────────── */
  function initSearchInput() {
    var termEl = document.getElementById('term');
    var formEl = document.getElementById('steam-search-form');
    if (!termEl) return;

    /* Read initial value */
    state.searchQ = termEl.value.trim();

    var debounce;
    termEl.addEventListener('input', function () {
      state.searchQ = this.value.trim();
      state.page = 1;

      if (IS_SEARCH) {
        /* On search page — debounce AJAX update */
        clearTimeout(debounce);
        debounce = setTimeout(doRefresh, 500);
      }
      /* On collection page — wait for form submit or Enter */
    });

    if (formEl) {
      formEl.addEventListener('submit', function (e) {
        e.preventDefault();
        state.searchQ = termEl.value.trim();
        state.page = 1;

        if (IS_SEARCH) {
          doRefresh();
        } else {
          /* Redirect collection page to search */
          window.location.href = '/search?q=' + encodeURIComponent(state.searchQ) + '&type=product';
        }
      });
    }
  }

  /* ── Price slider ────────────────────────────────────────────── */
  function initPriceSlider() {
    var slider  = document.getElementById('price_range');
    var display = document.getElementById('price_range_display');
    if (!slider) return;

    function updateLabel() {
      var idx  = parseInt(slider.value, 10);
      var stop = PRICE_STOPS[idx] || PRICE_STOPS[PRICE_STOPS.length - 1];
      if (display) display.textContent = stop.label;
      state.maxPrice = stop.value;
    }

    slider.addEventListener('input', updateLabel);
    slider.addEventListener('change', function () {
      updateLabel();
      state.page = 1;
      if (IS_COLLECTION) {
        /* For collection: price filter is client-side since path-based tags can't also carry price server-side cleanly */
        doRefresh();
        setTimeout(applyClientPriceFilter, 600);
      } else {
        doRefresh();
        setTimeout(applyClientPriceFilter, 600);
      }
    });

    /* Set initial slider to max (Any Price) */
    slider.value = PRICE_STOPS.length - 1;
    updateLabel();
  }

  /* ── Tag filter rows ─────────────────────────────────────────── */
  function initTagFilters() {
    /* Include / toggle */
    document.querySelectorAll('.tab_filter_control_row[data-filter-type="tag"]').forEach(function (row) {
      var inclCtrl = row.querySelector('.tab_filter_control_include');
      if (!inclCtrl) return;

      inclCtrl.addEventListener('click', function (e) {
        e.stopPropagation();

        var tagValue = row.dataset.tagValue;
        if (!tagValue) return;

        var isChecked = state.tags.indexOf(tagValue) !== -1;

        if (isChecked) {
          /* deselect */
          state.tags = state.tags.filter(function (t) { return t !== tagValue; });
          row.classList.remove('checked');
          inclCtrl.classList.remove('checked');
        } else {
          /* select */
          state.tags.push(tagValue);
          row.classList.add('checked');
          inclCtrl.classList.add('checked');
          /* remove from excludes if present */
          var notBtn = row.querySelector('.tab_filter_control_not');
          if (notBtn) notBtn.classList.remove('checked');
        }

        state.page = 1;
        scheduleRefresh(0);
      });
    });

    /* Exclude (red ✕) */
    document.querySelectorAll('.tab_filter_control_not').forEach(function (notBtn) {
      notBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        var row = notBtn.closest('.tab_filter_control_row');
        if (!row) return;
        var tagValue = row.dataset.tagValue;
        if (!tagValue) return;

        var isExcluded = notBtn.classList.contains('checked');

        if (isExcluded) {
          notBtn.classList.remove('checked');
        } else {
          notBtn.classList.add('checked');
          /* also remove from includes */
          state.tags = state.tags.filter(function (t) { return t !== tagValue; });
          row.classList.remove('checked');
          var inclCtrl = row.querySelector('.tab_filter_control_include');
          if (inclCtrl) inclCtrl.classList.remove('checked');
        }
        /* NOTE: Shopify doesn't natively exclude tags from collection results.
           This updates the visual state but cannot filter server-side for excludes.
           The JS hides matching rows client-side: */
        applyExcludeFilter();
      });
    });
  }

  function applyExcludeFilter() {
    /* Hide rows whose product title or data includes an excluded tag */
    var excluded = [];
    document.querySelectorAll('.tab_filter_control_not.checked').forEach(function (btn) {
      var row = btn.closest('.tab_filter_control_row');
      if (row && row.dataset.tagLabel) excluded.push(row.dataset.tagLabel.toLowerCase());
    });

    document.querySelectorAll('.search_result_row').forEach(function (row) {
      if (excluded.length === 0) { row.style.display = ''; return; }
      /* We can only do client-side by product title heuristic.
         Proper exclude needs product tags in DOM — we add data-tags on each row. */
      var tagAttr = row.dataset.tags || '';
      var rowTags = tagAttr.toLowerCase();
      var hide = excluded.some(function (ex) { return rowTags.indexOf(ex) !== -1; });
      row.style.display = hide ? 'none' : '';
    });
  }

  /* ── Collapsible sidebar blocks ──────────────────────────────── */
  function initCollapsibleBlocks() {
    document.querySelectorAll('#additional_search_options .block_header').forEach(function (header) {
      header.style.cursor = 'pointer';
      header.addEventListener('click', function () {
        var block = header.closest('.block');
        if (block) block.classList.toggle('collapsed');
      });
    });
  }

  /* ── "See All" tag list expander ─────────────────────────────── */
  function initTagSeeAll() {
    var link = document.getElementById('tag_filter_see_all');
    if (!link) return;
    link.addEventListener('click', function (e) {
      e.preventDefault();
      var container = document.getElementById('TagFilter_Container');
      if (!container) return;
      container.classList.toggle('expanded');
      link.textContent = container.classList.contains('expanded') ? 'See less' : 'See all';
    });
  }

  /* ── Pagination links ────────────────────────────────────────── */
  function bindPaginationLinks() {
    document.querySelectorAll('.search-page-link').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var p = parseInt(link.dataset.page, 10);
        if (!isNaN(p)) {
          state.page = p;
          doRefresh();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    });
  }

  /* ── Browser back / forward ──────────────────────────────────── */
  window.addEventListener('popstate', function () {
    /* Re-read state from URL and refresh */
    readStateFromURL();
    doRefresh();
  });

  function readStateFromURL() {
    var params = new URLSearchParams(window.location.search);
    state.sortBy  = params.get('sort_by') || '';
    state.page    = parseInt(params.get('page') || '1', 10);
    state.searchQ = params.get('q') || '';

    /* Read tags from path for collection pages */
    if (IS_COLLECTION) {
      var pathParts = window.location.pathname.replace(/^\/collections\/[^/]+\/?/, '');
      state.tags = pathParts ? pathParts.split('+').filter(Boolean) : [];
    }

    /* Sync sort dropdown */
    var sortEl = document.getElementById('steam_sort_select');
    if (sortEl) sortEl.value = state.sortBy;

    /* Sync search input */
    var termEl = document.getElementById('term');
    if (termEl && state.searchQ) termEl.value = state.searchQ;
  }

  /* ── Init ────────────────────────────────────────────────────── */
  function init() {
    readStateFromURL();
    initSort();
    initSearchInput();
    initPriceSlider();
    initTagFilters();
    initCollapsibleBlocks();
    initTagSeeAll();
    bindPaginationLinks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
